import * as APITypes from "~/API";
import { getServiceRequestList } from "~/services/server";
import { buildPropertyMap, buildUnitMap, buildBuildingMap, buildParcelMap } from "../helpers";
import type { EntityListData, EntityListOptions } from "~/types";

export const formatServiceRequestOptions = (
  serviceRequestList: APITypes.ServiceRequest[],
  propertyMap: Record<string, APITypes.Property>,
  unitMap: Record<string, APITypes.Unit>,
  buildingMap: Record<string, APITypes.Building>,
  parcelMap: Record<string, APITypes.Parcel> = {}
): EntityListOptions[] => {
  return serviceRequestList.map((request) => {
    const title = request.title || "Untitled Request";

    // Determine context based on entityType
    if (request.entityType === APITypes.EntityType.Unit) {
      const unit = unitMap[request.entityID];
      const building = buildingMap[unit?.buildingID];
      const property = propertyMap[building?.propertyID];
      return {
        value: request.id,
        text: `SR: ${title} - Unit: ${unit?.name || "Unknown"}, Property: ${
          property?.name || "Unknown"
        }`,
      };
    } else if (request.entityType === APITypes.EntityType.Property) {
      const property = propertyMap[request.entityID];
      return {
        value: request.id,
        text: `SR: ${title} - Property: ${property?.name || "Unknown"}`,
      };
    } else if (request.entityType === APITypes.EntityType.Building) {
      // Abstract building - show only property
      const building = buildingMap[request.entityID];
      const property = propertyMap[building?.propertyID];
      return {
        value: request.id,
        text: `SR: ${title} - Building: ${
          building?.name || "Unknown"
        }- Property: ${property?.name || "Unknown"}`,
      };
    } else if (request.entityType === APITypes.EntityType.Parcel) {
      const parcel = parcelMap[request.entityID];
      const property = parcel?.propertyID
        ? propertyMap[parcel.propertyID]
        : undefined;
      return {
        value: request.id,
        text: `SR: ${title} - Parcel: ${parcel?.name || "Unknown"}, Property: ${
          property?.name || "Unknown"
        }`,
      };
    }

    // Fallback
    return {
      value: request.id,
      text: `SR: ${title}`,
    };
  });
};

export const getServiceRequestEntityList = async (
  ownerID: string,
  ownerType: APITypes.OwnerType
): Promise<EntityListData> => {
  const serviceRequestList = await getServiceRequestList({
    ownerID,
    ownerType,
  });

  // Build maps for all resource types that service requests might reference
  // This enables proper formatting with property/unit context
  const [propertyMap, unitMap, buildingMap, parcelMap] = await Promise.all([
    buildPropertyMap(ownerID, ownerType),
    buildUnitMap(ownerID, ownerType),
    buildBuildingMap(ownerID, ownerType),
    buildParcelMap(ownerID, ownerType),
  ]);

  return {
    propertyList: [],
    buildingList: [],
    unitList: [],
    serviceRequestList: serviceRequestList.data,
    amenityList: [],
    propertyMap,
    buildingMap,
    unitMap,
    parcelMap,
  };
};
